import { QueryClient } from '@tanstack/react-query';
import { getConfigByKey as _getConfigByKey } from '@tn/configs';
import { Translate } from '@tn/foundation/hooks/providers/useTranslation';
import { TrackingContextType } from '@tn/foundation/providers/TrackingProvider';
import { cloneDeep } from 'lodash-es';
import { NextRouter } from 'next/router';
import {
    AnyStateMachine,
    NonReducibleUnknown,
    UnknownActorLogic,
    UnknownMachineConfig,
} from 'xstate';

import {
    BasicInvokeConfig,
    EntitiesMapping,
    FlowEntity,
    FlowId,
    SubEntitiesMapping,
} from '../types';
import { createRegionAwareFlowRouter } from '../utils/createRegionAwareFlowRouter';

import { flowEntityMap } from '.';

// created to address flowConfig.invoke.id typing
function isSingle<T>(value: readonly T[] | T): value is T {
    return !Array.isArray(value);
}

export const composeStateMachineRecursive = ({
    id,
    getConfigByKey,
    router,
    mapping,
    queryClient,
    region,
    trackEvent,
    t,
}: {
    id: string;
    getConfigByKey: ReturnType<typeof _getConfigByKey>;
    router: NextRouter;
    mapping: EntitiesMapping | SubEntitiesMapping;
    queryClient: QueryClient;
    region: string;
    trackEvent: TrackingContextType['trackEvent'];
    t: Translate['t'];
}) => {
    const processInvokes = (
        invokes: NonNullable<UnknownMachineConfig['invoke']>,
        subEntities: Record<string, { entity: FlowEntity }>,
    ) => {
        let invokesToProcess: UnknownMachineConfig['invoke'] = [];
        if (isSingle(invokes)) {
            invokesToProcess = [invokes];
        } else {
            invokesToProcess = invokes;
        }

        const newActors: Record<string, UnknownActorLogic> = {};
        const newInvokes: BasicInvokeConfig[] = [];
        invokesToProcess.forEach((inv, idx) => {
            // Filling new invokes array
            // with input function for actor invoke
            newInvokes[idx] = {
                ...inv,
                // Actor input must be defined on the invoke itself as input
                // may differ based on the use case
                input: (params) => {
                    const result = {
                        ...((inv.input as Extract<typeof inv.input, NonReducibleUnknown>) || {}),
                        ...((subEntities[inv.id!]?.entity?.inputActor as CallableFunction)?.(
                            params,
                        ) ?? {}),
                    };
                    return result;
                },
            } as typeof inv;

            // Filling invoked actors map for current actor
            newActors[inv.id!] = composeStateMachineRecursive({
                id: inv.id!,
                getConfigByKey,
                router,
                mapping: subEntities,
                queryClient,
                region,
                trackEvent,
                t,
            });
        });
        return { newActors, newInvokes };
    };
    const processStates = (states: NonNullable<UnknownMachineConfig['states']>) => {
        const newStates: UnknownMachineConfig['states'] = {};
        Object.entries(states).forEach(([key, state]) => {
            const newState = { ...state };
            if (state?.invoke) {
                const { newInvokes, newActors } = processInvokes(state.invoke, subEntities);
                fillActors(newActors);

                newState.invoke = newInvokes;
            }
            if (state.states) {
                newState.states = processStates(state.states);
            }

            newStates[key] = newState;
        });
        return newStates;
    };

    const { entity, subEntities } = mapping[id as keyof typeof mapping] as {
        entity: FlowEntity;
        subEntities: Record<string, { entity: FlowEntity }>;
    };

    const entityMachineConfig = getConfigByKey(`${id}.machineConfig`) as UnknownMachineConfig;
    if (!entityMachineConfig) {
        return (entity as FlowEntity).initActor({
            type: 'non-machine',
            queryClient,
            router,
            region,
            getConfigByKey,
            trackEvent,
            t,
        });
    }

    const clonedConfig = cloneDeep(entityMachineConfig);
    const actors: Record<string, UnknownActorLogic> = {};
    const fillActors = (acts: Record<string, UnknownActorLogic>) => {
        Object.entries(acts).forEach(([key, act]) => {
            actors[key] = act;
        });
    };

    // Actor output when available must be set to the root of the configuration
    clonedConfig.output = entity.outputActor;

    // Actor root invokes must but handled first
    // for ease of use we always ultimately convert invokes to arrays
    // as they're easier to work with
    if (clonedConfig.invoke) {
        const { newInvokes, newActors } = processInvokes(clonedConfig.invoke, subEntities);
        fillActors(newActors);
        clonedConfig.invoke = newInvokes;
    }

    // We cycle all state and eventually their invokes
    if (clonedConfig.states) {
        const newStates = processStates(clonedConfig.states);
        clonedConfig.states = newStates;
    }

    return (entity as FlowEntity).initActor({
        type: 'machine',
        config: clonedConfig as UnknownMachineConfig,
        actors,
        router,
        queryClient,
        region,
        getConfigByKey,
        trackEvent,
        t,
    }) as AnyStateMachine;
};

export const composeStateMachine = ({
    flowId,
    getConfigByKey,
    router,
    queryClient,
    region,
    trackEvent,
    t,
}: {
    flowId: FlowId;
    getConfigByKey: ReturnType<typeof _getConfigByKey>;
    router: NextRouter;
    queryClient: QueryClient;
    region: string;
    trackEvent: TrackingContextType['trackEvent'];
    t: Translate['t'];
}) => {
    const regionAwareRouter = createRegionAwareFlowRouter(
        router,
        region,
        getConfigByKey,
        queryClient,
    );

    const composedMachine = composeStateMachineRecursive({
        id: flowId,
        getConfigByKey,
        router: regionAwareRouter,
        mapping: flowEntityMap,
        queryClient,
        region,
        trackEvent,
        t,
    });

    return composedMachine as AnyStateMachine;
};
